<template>
    <app-layout title="Checkout">
        <cart-error-modal 
            :show="showCartError" 
            @close="hideCartError"
        />
        <div class="lg:flex">
            <sidebar-catalogue :categories="categories" class="z-40 relative" />

            <div class="checkout flex-1 relative z-30">
                <div class="p-4 xl:p-8" v-if="!isFetching">
                    <h1 class="text-xl lg:text-3xl">Checkout - {{ activeCustomer.name }}</h1>

                    <ul v-if="cartChanges && cartChanges.length && !dismissedCartChanges" class="my-4">
                        <li v-for="(change, index) in cartChanges" :key="index"
                            class="border rounded-md p-2 my-1 relative"
                            :class="change.style === 'error' ? 'bg-red-100 border-red-300' : 'bg-blue-100 border-blue-300'"
                        >
                            <a href="#" @click.prevent="dismissCartChanges" class="absolute top-1 right-1"><i class="fas fa-times-circle"></i></a>
                            
                            {{ change.message}}

                            <ul v-if="change.products && change.products.length" class="list-disc ml-10">
                                <li v-for="(product, index) in change.products" :key="'product' + index">{{ product }}</li>
                            </ul>
                        </li>
                    </ul>

                    <shopping-cart :isTaxExemptCustomer="isTaxExemptCustomer" :delivery_fee="deliveryFee" :appliableCreditAmount="appliableCreditAmount"/>

                    <div v-show="isSchoolCustomer" class="pt-1 sm:pt-2 pb-0 lg:px-8 sm:font-semibold text-gray-500 text-xs xl:text-sm text-center">
                        <div class="sm:hidden">Order deadline is Wed. @ 11:59pm for delivery the next week</div>
                        <div class="hidden sm:block sm:text-sm">Order deadline is Wednesday at 11:59pm for delivery the following week</div>
                    </div>

                    <form @submit.prevent="handleSubmit" class="mt-16">
                        <div class="flex gap-x-8 flex-wrap">
                            <div class="form-group w-full md:w-auto">
                                <jet-label value="Shipping Method" />
                                <div class="flex w-40 sm:w-52">
                                    <multiselect class="multi-lg h-7 text-base"
                                        v-model="form.shipping_method"
                                        :options="shippingOptions"
                                        valueProp="value"
                                        trackBy="value"
                                        label="label"
                                        :canClear="false"
                                        :canDeselect="false"
                                        @input="fetchOpenOrder"
                                    />
                                    <jet-input-error :message="$page.props.errors.shipping_method" class="m1" />
                                </div>
                            </div>

                            <div class="form-group w-full md:w-auto">
                                <jet-label value="Ship Date" />
                                <div class="flex gap-x-6 flex-wrap">
                                    <datepicker class=" w-40 sm:w-52" inputClassName="dp-custom-input"
                                        v-model="form.ship_date"
                                        weekStart="0"
                                        monthNameFormat="long"
                                        :enableTimePicker="false"
                                        :autoApply="true"
                                        :is24="false"
                                        :clearable="false"
                                        :minDate="minDate"
                                        :maxDate="maxDate"
                                        :disabled-dates="invalidDeliveryDates"
                                        :format="formatDate"
                                        @update:modelValue="fetchOpenOrder()"
                                    />
                                    <div v-show="openOrder" class="w-full md:w-auto mt-2 md:mt-0">
                                        <div v-show="isSchoolCustomer" class="flex items-center">
                                            <div class="px-2 leading-tight" style="margin-top: -3px;">
                                                <div class="font-semibold">This will be added to your order #{{ form.add_to_order_id }}</div>
                                                <div class="text-xs">
                                                    Ordered by {{ openOrder && openOrder.purchaser ? openOrder.purchaser.name : '' }} on {{ openOrder ? formatOrderDate(openOrder.order_date) : '' }}
                                                </div>
                                            </div>
                                        </div>
                                        <div v-show="!isSchoolCustomer" class="flex items-center">
                                            <input type="checkbox" class="rounded h-6 w-6" name="add_to_order" v-model="form.add_to_order"/>
                                            <div class="px-2 leading-tight" style="margin-top: -3px;">
                                                <div class="font-semibold">Add to your order #{{ form.add_to_order_id }}</div>
                                                <div class="text-xs">
                                                    Ordered by {{ openOrder && openOrder.purchaser ? openOrder.purchaser.name : '' }} on {{ openOrder ? formatOrderDate(openOrder.order_date) : '' }}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <jet-input-error :message="form.errors.ship_date" />
                                <div v-if="cartHasSpecialOrderItems && specialOrderDeliveryDate" class="ml-3 leading-loose">
                                    *Special order items will be {{ form.shipping_method === 'pickup' ? 'ready for pickup' : 'delivered' }}
                                    on {{ specialOrderDeliveryDate }}
                                </div>
                                <jet-input-error v-if="form.errors.ship_date !== form.errors.special_order_ship_date" :message="form.errors.special_order_ship_date" />
                            </div>
                        </div>

                        <div class="form-group w-full max-w-md">
                            <jet-label value="PO #" />
                            <jet-input
                                type="text"
                                v-model="form.po_num"
                                class="w-full"
                                placeholder="Enter PO # (optional)"
                            />
                            <jet-input-error :message="form.errors.po_num" />
                        </div>
                        
                        <div class="form-group">
                            <jet-label value="Order Notes / Shipping Instructions" />
                            <textarea v-model="form.notes" class="rounded w-full max-w-2xl h-28"></textarea>
                            <jet-input-error :message="form.errors.notes" />
                        </div>

                        <jet-button type="submit" :disabled="isAdmin || (form.processing || checkoutSubmitLocked || !canPlaceOrder)">Place Order</jet-button>

                        <span v-if="!canPlaceOrder" class="ml-4 text-red-600">
                            Order minimum of {{ toCurrency(parseFloat(orderMinimum)) }} not met.
                        </span>
                        <span v-else-if="!minimumMet && !cartIsOnlyGiftCards && !fixedDeliveryFeeOverrideActive" class="ml-4 text-gray-500">
                            A delivery fee of {{ toCurrency(parseFloat(deliveryFeeBelowMinimum)) }} applies to deliveries below {{ toCurrency(parseFloat(orderMinimum)) }}.
                        </span>
                    </form>
                </div>

                <div v-else class="loading">
                    <div class="text-center text-lg font-bold flex flex-col items-center justify-center min-h-screen">
                        <img src="/images/icons/loading-pulse.svg" height="128" width="128" class="" />
                        <p>Loading ...</p>
                    </div>
                </div>
            </div>
        </div>

    </app-layout>
</template>

<script>
import { onMounted } from 'vue';
import { useForm, usePage } from '@inertiajs/inertia-vue3'
import AppLayout from '@/Layouts/AppLayout'
import SidebarCatalogue from '@/Layouts/SidebarCatalogue';
import ShoppingCart from '@/Pages/Checkout/Partials/ShoppingCart';
import JetButton from '@/Jetstream/Button';
import JetLabel from '@/Jetstream/Label';
import JetInput from '@/Jetstream/Input';
import JetInputError from '@/Jetstream/InputError';
import UseCart from '@/Composables/UseCart';
import CartErrorModal from '@/Components/CartErrorModal';
import Datepicker from '@vuepic/vue-datepicker';
import { DateTime } from "luxon";
import Multiselect from '@vueform/multiselect';
import UseUserAuth from '@/Composables/UseUserAuth';
import { fetchCheckoutShipDateConstraints } from '@/Composables/useWholesaleShipDateConstraints';

import '@vueform/multiselect/themes/default.css';
import '@vuepic/vue-datepicker/dist/main.css';


export default {
    components: {
        AppLayout,
        CartErrorModal,
        Datepicker,
        SidebarCatalogue,
        ShoppingCart,
        JetButton,
        JetLabel,
        JetInput,
        JetInputError,
        Multiselect,
    },

    props: ['categories', 'holidays', 'cartChanges', 'orderMinimum', 'deliveryFeeBelowMinimum', 'skipsDeliveryMinimumRules'],

    setup (props) {
        const { isAdmin } = UseUserAuth(usePage().props.value.user);
        const form = useForm({
            shipping_method: usePage().props.value.activeCustomer.default_shipping_method,
            allow_substitutions: false,
            ship_date: null,
            special_order_ship_date: null,
            add_to_order: false,
            add_to_order_id: null,
            notes: usePage().props.value.activeCustomer.order_notes,
            po_num: usePage().props.value.activeCustomer.po_num ?? null,
        });

        const { fetchCart, isFetching, cartTotal, cartHasSpecialOrderItems, cartCategories, showCartError, hideCartError } =
            UseCart(
                usePage().props.value.user,
                usePage().props.value.activeCustomer ? usePage().props.value.activeCustomer.tax_exempt : 0
            );

        onMounted(fetchCart);

        return { form, fetchCart, isFetching, cartTotal, cartHasSpecialOrderItems, cartCategories, showCartError, hideCartError, isAdmin };
    },

    data() {
        return {
            shippingOptions: [
                { value: 'delivery', label: 'Delivery' },
                { value: 'pickup', label: 'Pickup' },
            ],

            openOrder: null,
            dismissedCartChanges: false,
            checkoutSubmitLocked: false,
            shipDateConstraints: null,
        }
    },

    methods: {

        async refetchShipDateConstraints() {
            if (!this.activeCustomer) {
                return;
            }
            try {
                this.shipDateConstraints = await fetchCheckoutShipDateConstraints(this.form.shipping_method);
                this.$nextTick(() => {
                    this.setDefaultDeliveryDate();
                });
            } catch (e) {
                console.error(e);
            }
        },

        fetchOpenOrder(shipping_method = this.form.shipping_method, date = this.form.ship_date, ) {

            axios
                .get(route('customer_orders.fetch', {
                    date: DateTime.fromJSDate(date).toFormat('yyyy-LL-dd'),
                    shipping_method: shipping_method
                }))
                .then(response => {
                    this.openOrder = response.data && response.data !== null ? response.data : null;
                    this.form.add_to_order = response.data ? true : false;
                    this.form.add_to_order_id = response.data ? response.data.id : null;
                })
                .catch(error => {
                    console.log(error);
                });
        },

        handleSubmit () {
            if (this.checkoutSubmitLocked || this.form.processing) {
                return;
            }
            this.checkoutSubmitLocked = true;
            this.form.transform(data => {
                const ship_date = data.ship_date ?
                    DateTime.fromJSDate(data.ship_date).toFormat('yyyy-LL-dd') :
                    null;

                return {
                    ...data,
                    ship_date,
                }
            })
            .post(this.route('checkout.store'), {
                onSuccess: () => {
                    this.fetchCart();
                    this.setDefaultDeliveryDate();
                },
                onFinish: () => {
                    this.checkoutSubmitLocked = false;
                },
            });
        },

        setDefaultDeliveryDate() {
            if (this.form.ship_date != null) {
                return;
            }
            if (!this.activeCustomer || !this.shipDateConstraints?.min_date || !this.shipDateConstraints?.max_date) {
                return;
            }

            const min = DateTime.fromISO(this.shipDateConstraints.min_date);
            const max = DateTime.fromISO(this.shipDateConstraints.max_date);
            const disabled = new Set(this.shipDateConstraints.disabled_dates || []);

            for (let d = min; d <= max; d = d.plus({ days: 1 })) {
                const ymd = d.toFormat('yyyy-LL-dd');
                if (!disabled.has(ymd)) {
                    this.form.ship_date = d.toJSDate();
                    this.fetchOpenOrder(this.form.shipping_method, d.toJSDate());
                    return;
                }
            }
        },

        formatDate(date) {
            return DateTime.fromJSDate(date).toFormat('yyyy-LL-dd');
        },

        formatOrderDate(date) {
            return DateTime.fromISO(date).toFormat('MMM d, yyyy');
        },

        dismissCartChanges () {
            this.dismissedCartChanges = true;
        }
    },

    computed: {
        minDate () {
            if (this.shipDateConstraints?.min_date) {
                return DateTime.fromISO(this.shipDateConstraints.min_date).toJSDate();
            }
            return DateTime.now().toJSDate();
        },

        maxDate () {
            if (this.shipDateConstraints?.max_date) {
                return DateTime.fromISO(this.shipDateConstraints.max_date).toJSDate();
            }
            return DateTime.now().plus({ months: 3 }).toJSDate();
        },

        invalidDeliveryDates() {
            if (!this.shipDateConstraints?.disabled_dates) {
                return [];
            }
            return this.shipDateConstraints.disabled_dates.map((d) => DateTime.fromISO(d).toJSDate());
        },

        invalidDeliveryDatesYMD () {
            return this.shipDateConstraints?.disabled_dates || [];
        },

        specialOrderDeliveryDate() {
            if (!this.cartHasSpecialOrderItems || this.form.ship_date == null || !this.shipDateConstraints) {
                return false;
            }

            const minSpec = DateTime.fromISO(this.shipDateConstraints.special_order_min_date).startOf('day');
            const shipDate = DateTime.fromJSDate(this.form.ship_date).startOf('day');

            if (shipDate >= minSpec) {
                this.form.special_order_ship_date = shipDate.toFormat('yyyy-LL-dd');
                return false;
            }

            const disabled = new Set(this.shipDateConstraints.disabled_dates || []);
            const max = DateTime.fromISO(this.shipDateConstraints.max_date);
            let d = minSpec;

            while (d <= max) {
                const ymd = d.toFormat('yyyy-LL-dd');
                if (!disabled.has(ymd)) {
                    this.form.special_order_ship_date = ymd;
                    return d.toLocaleString({ month: 'long', day: 'numeric' });
                }
                d = d.plus({ days: 1 });
            }

            return false;
        },

        activeCustomer() {
            return this.$page.props.activeCustomer;
        },

        isSchoolCustomer() {
            return this.activeCustomer?.type === 'School';
        },

        isTaxExemptCustomer() {
            return this.activeCustomer?.tax_exempt;
        },

        dairyCategory() {
            let category = this.categories.find(c => c.id == 96);

            if(category === undefined) return null;

            return category.id;
        },

        cartHasDairyItems() {
            if(this.dairyCategory === null) return false;

            return this.cartCategories.has(this.dairyCategory);
        },

        fixedDeliveryFeeOverrideActive() {
            if (this.skipsDeliveryMinimumRules === true) {
                return true;
            }
            const v = parseFloat(this.activeCustomer?.delivery_fee_override);
            return !Number.isNaN(v) && v > 0;
        },

        canPlaceOrder() {
            // Pickup
            if(this.form.shipping_method === 'pickup') return true;
            else if (this.fixedDeliveryFeeOverrideActive) return true;
            // Delivery
            else if (this.cartTotal >= this.orderMinimum) return true;
            else if (this.deliveryFeeBelowMinimum !== null && this.deliveryFeeBelowMinimum !== '') return true;
            else return false;
        },

        minimumMet() {
            if(this.form.shipping_method === 'pickup') return true;
            return this.form.shipping_method === 'delivery' && this.cartTotal >= this.orderMinimum;
        },

        deliveryFee() {
            if (this.form.shipping_method === 'pickup') {
                return null;
            }
            if (this.cartIsOnlyGiftCards) {
                return null;
            }
            if (this.fixedDeliveryFeeOverrideActive) {
                const o = parseFloat(this.activeCustomer.delivery_fee_override);
                return Number.isNaN(o) ? null : o;
            }
            if (this.deliveryFeeBelowMinimum === null || this.deliveryFeeBelowMinimum === '' || this.minimumMet) {
                return null;
            }
            return this.deliveryFeeBelowMinimum;
        },

        appliableCreditAmount () {
            // if (this.activeCustomer && this.activeCustomer.auto_apply_credit && this.activeCustomer.credit_balance !== null)
            // {
            //     return this.roundDecimals(parseFloat(this.activeCustomer.credit_balance));
            // }
            return 0;
        },

        cartIsOnlyGiftCards() {
            return this.cartCategories?.has(165);
        },

    },

    watch: {
        activeCustomer: {
            handler(newVal, oldVal) {
                if(newVal.id !== oldVal.id) {
                    this.fetchCart();
                    this.form.ship_date = null;
                    this.form.notes = newVal.order_notes;
                }
            },
            deep: true
        },
        isFetching(newVal, oldVal) {
            if (oldVal === true && newVal === false) {
                this.refetchShipDateConstraints();
            }
        },
        'form.shipping_method' : {
            async handler(newVal, oldVal) {
                await this.refetchShipDateConstraints();
                if(oldVal === 'pickup' && newVal === 'delivery') {
                    let shipDate = DateTime.fromJSDate(this.form.ship_date).toFormat('yyyy-LL-dd');

                    let invalidDate = this.invalidDeliveryDates.find(i => {
                        return shipDate === DateTime.fromJSDate(i).toFormat('yyyy-LL-dd');
                    });

                    if(invalidDate !== undefined || (DateTime.fromJSDate(this.form.ship_date) < DateTime.fromJSDate(this.minDate))) {
                        this.form.ship_date = null;
                    }
                }
            }
        },
        cartChanges: {
            handler(newVal) {
                if (newVal && newVal.length > 0) {
                    this.dismissedCartChanges = false;
                }
            },
            deep: true
        },
        cartHasSpecialOrderItems: {
            handler() {
                this.refetchShipDateConstraints();
            },
        },
    },

    mounted () {
    },
}
</script>

<style>
.dp-custom-input {
    @apply h-8;
}
</style>

<style scoped>
.loading {
    @apply absolute top-0 left-0 right-0 bottom-0 bg-white bg-opacity-75;
}

@media (min-width: 1024px) {
    .checkout {
        padding-left: 240px;
    }

    .loading {
        left: 240px;
    }
}
</style>
